import {
  forwardRef,
  useState,
  useEffect,
  SyntheticEvent,
} from "react";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import Title from "../../../components/Title";
import { Office } from "../../../models/office";
import { Collect } from "../../../models/collect";
import { Expense } from "../../../models/expense";
import { Type_Operation } from "../../../models/type_operation";
import { Link, useNavigate } from 'react-router-dom';
import { Add as AddIcon, ListAlt as ListAltIcon } from '@mui/icons-material';
import { useSelection } from '../../../components/context/SelectionContext';
import { CardActions, FormControlLabel, Switch, useTheme, useMediaQuery, Divider, TextField, Typography, Button, Box, Card, Grid, FormControl, MenuItem, InputLabel, Select, CardContent, Tooltip } from '@mui/material';
import HeaderResponsive from "../../../components/layouts/HeaderResponsive";
import moment from "moment-timezone";
import PermissionButton from "../../../components/common/PermissionButton";
import { useResourcePermissions } from "../../../hooks/useResourcePermissions";

const CustomInput = forwardRef((props, ref) => {
  return (
    <TextField
      fullWidth
      {...props}
      inputRef={ref}
      size="small"
      label="Date"
      autoComplete="off"
    />
  );
});

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const CajaPrincipalAdd: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  // ** States
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { canView: canViewMainCashMovements } = useResourcePermissions('/administrar/cajas');
  const [operation_id, setOperation_id] = useState("");
  const [box_id, setBox_id] = useState("");
  const [balance, setBalance] = useState("");
  const [balanceCollect, setBalanceCollect] = useState("");
  const [collectsOffice, setCollectsOffice] = useState([]);
  const [box, setBox] = useState([]);
  const [boxOfficeId, setBoxOfficeId] = useState<number | null>(null);
  const [boxCollectId, setBoxCollectId] = useState<number | null>(null);
  const [value, setValue] = useState("");
  const [description, setDescripcion] = useState("");
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [expensesid, setExpensesId] = useState<number | null>(null);
  const [operation, setOperation] = useState([]);
  const [offices, setOffices] = useState([]);
  const [out, setOut] = useState(false);
  // En esta vista no se permite "retirar de cobro". Solo se permite enviar a cobro.
  // Por lo tanto, no se exponen switches: la dirección queda fija.
  const { office_id, setOffice_id, collect_id, setCollect_id, user_id, setUser_id } = useSelection();

  const [boxOrigen, setBoxOrigen] = useState<number | null>(null);
  const [boxDestination, setBoxDestination] = useState<number | null>(null);

  useEffect(() => {
    // Dirección fija:
    // - operation_id=2: ENVIAR a cobro (origen oficina -> destino cobro)
    // - otros: comportamiento existente (origen oficina, sin destino por defecto)
    if (operation_id === "2") {
      setBoxOrigen(boxOfficeId);
      setBoxDestination(boxCollectId);
      return;
    }
    setBoxOrigen(boxOfficeId);
    setBoxDestination(null);
  }, [operation_id, boxCollectId, boxOfficeId]);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!office_id || !operation_id || !value || !expensesid) {
      showAlert('Por favor, complete todos los campos obligatorios!', 'warning');
      return;
    }
    // Solo se permite enviar a cobro (operation_id=2), por lo que aquí sí exigimos cobro.
    if (operation_id === "2" && !collect_id) {
      showAlert('Por favor, seleccione un cobro!', 'warning');
      return;
    }

    const expenseDate = moment().tz('America/Bogota').format('YYYY-MM-DD');
    const formData = {
      description: description,
      value: parseInt(value),
      box_origin_id: boxOrigen,
      box_destination_id: boxDestination,
      expense_id: expensesid,
      user_id,
      type_operation_id: Number(operation_id),
      date: expenseDate,
      out: out,
    };
    const url = "/boxmovement";

    try {
      const response = await axios.post(url, formData);
      showAlert('Se ha realizado el registro exitosamente!', 'success');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate('/administrar/cajas');
    } catch (error) {
      showAlert('Error fetching data Expense', 'error');
    } finally {
      hideLoading();
    }
  };

  const fetchBalance = async (officeId: string) => {
    try {
      const { data } = await axios.get(`/boxoffice/${officeId}`);
      setBalance(data.data[0].balance);
      setBoxOfficeId(data.data[0].id);
    } catch (error) {
      showAlert('Error fetching balance!', 'error');
    }
  };

  const fetchBalanceCollect = async (collectId: number) => {
    showLoading();
    try {
      const { data } = await axios.get(`/boxcollect/${collectId}`);
      const first = data?.data?.[0];
      if (!first) {
        setBalanceCollect("0");
        setBoxCollectId(null);
        return;
      }
      setBalanceCollect(first.balance);
      setBoxCollectId(first.id);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Esperar 1 segundo
    } catch (error) {
      const serverMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.response?.data?.error;
      showAlert(
        serverMessage || 'Error fetching collect balance!',
        'error',
      );
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const [
          { data: userData },
          { data: officesData },
          { data: operationData },
        ] = await Promise.all([
          axios.get("user"),
          axios.get("offices"),
          axios.get("typeoperation"),
        ]);
        setUser_id(userData.data.id);
        setOffices(officesData.data);
        setOperation(operationData.data);

        await new Promise(resolve => setTimeout(resolve, 2000)); // Esperar 2 segundos
      } catch (error) {
        // showAlert('Alerta recuperar la información del usaurio!', 'warning');
        //console.log('Alerta recuperar la información del usaurio:', error);
      } finally {
        hideLoading();
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (office_id) {
        showLoading();
        try {
          const [
            { data: boxData },
            { data: collectsData },
          ] = await Promise.all([
            axios.get(`/boxoffice/${office_id}`),
            axios.get(`collectsoffice/${office_id}`),
          ]);

          if (boxData.data.length > 0) {
            setBox(boxData.data[0]);
            setBox_id(boxData.data[0].id);
            await fetchBalance(office_id); // Fetch balance after setting box data
          } else {
            showAlert('La oficina seleccionada no tiene saldo en caja disponible!', 'warning');
          }
          setCollectsOffice(collectsData);
          setCollect_id(""); // Limpiar el select de collect (usar cadena vacía)
          setBalanceCollect("0");
          setBoxCollectId(null);
        } catch (error) {
          showAlert('Error fetching data2!', 'error');
        } finally {
          hideLoading();
        }
      }
    };
    fetchData();
  }, [office_id]);

  useEffect(() => {
    if (collect_id) {
      fetchBalanceCollect(Number(collect_id)); // Convertir collect_id a número
    }
  }, [collect_id]);

  useEffect(() => {
    if (operation_id) {
      (async () => {
        try {
          const url = "/getexpensesbytypeops";
          const formDatas = {
            type_operation_id: Number(operation_id),
            type_expense_id: 2
          }
          // const { data } = await axios.get(`expenseoperation/${operation_id}`);
          const { data } = await axios.post(url, formDatas);
          setExpenses(data.data);
          setExpensesId(null); // Reiniciar el campo Detalle Operación
        } catch (error) {
          showAlert('No ha sido posible obtener los tipos de gastos!', 'error');
        }
      })();
    } else {
      setExpenses([]);
      setExpensesId(null);
    }
  }, [operation_id]);

  useEffect(() => {
    if (operation_id === "2") {
      setOut(true);
    } else {
      setCollect_id("");
      setBalanceCollect("0");
      setBoxCollectId(null);
      setBoxDestination(null);
      setOut(false);
    }
  }, [operation_id]);

  return (
    <HeaderResponsive >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Title>Registrar Movimiento Caja Principal</Title>
        <Box display="flex" gap={1} flexShrink={0}>
          <Tooltip title={canViewMainCashMovements ? "Movimientos Caja Principal" : "Sin permiso para ver movimientos"}>
            <PermissionButton
              allowed={canViewMainCashMovements}
              deniedTooltip="Sin permiso para ver movimientos"
              tooltip="Movimientos Caja Principal"
              component={Link}
              to="/administrar/cajas"
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={isMobile ? undefined : <ListAltIcon />}
              aria-label={isMobile ? 'Movimientos Caja Principal' : undefined}
            >
              {isMobile ? <ListAltIcon /> : 'Movimientos Caja Principal'}
            </PermissionButton>
          </Tooltip>
          <Tooltip title="Registrar gasto">
            <Button
              component={Link}
              to="/prestamos/gastos/add"
              size="small"
              variant="contained"
              color="primary"
              startIcon={isMobile ? undefined : <AddIcon />}
              aria-label={isMobile ? 'Registrar gasto' : undefined}
            >
              {isMobile ? <AddIcon /> : 'Registrar Gasto'}
            </Button>
          </Tooltip>
        </Box>
      </Box>
      <Card>
        <Divider sx={{ margin: 0 }} />
        <form onSubmit={(e) => e.preventDefault()}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="form-layouts-separator-select-label">
                    Seleccionar Oficina
                  </InputLabel>
                  {offices && offices.length > 0 ? (
                    <Select
                      label="Seleccionar Oficina"
                      value={office_id}
                      id="form-layouts-separator-select"
                      labelId="form-layouts-separator-select-label"
                      onChange={(event) => setOffice_id(event.target.value)}
                      size="small"
                      required
                    >
                      {offices.map((office: Office) => (
                        <MenuItem key={office.id} value={office.id}>
                          {office.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} style={{ textAlign: "right" }}>
                <TextField fullWidth size="small" label="Saldo Disponible Oficina" value={balance || 0} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="operation">Seleccionar Tipo Operación</InputLabel>
                  {operation && operation.length > 0 ? (
                    <Select
                      label="Operación"
                      value={operation_id}
                      id="operation_id"
                      labelId="operation_id"
                      onChange={(event) => setOperation_id(event.target.value)}
                      size="small"
                      required
                    >
                      {operation.map((op: Type_Operation) => (
                        <MenuItem key={op.id} value={op.id.toString()}>
                          {op.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
              {operation_id === "2" && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="collect">Seleccionar Cobro</InputLabel>
                    {collectsOffice && collectsOffice.length > 0 ? (
                      <Select
                        label="Seleccionar Cobro"
                        value={collect_id ? collect_id.toString() : ""}
                        id="collects_id"
                        labelId="collects_id"
                        onChange={(event) => setCollect_id(event.target.value)} // No es necesario parseInt
                        size="small"
                        required
                      >
                        {collectsOffice.map((collect: Collect) => (
                          <MenuItem key={collect.id} value={collect.id.toString()}>
                            {collect.name}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : null}
                  </FormControl>
                </Grid>
              )}
              {operation_id === "2" && (

                <Grid item xs={12} sm={6}>

                  <FormControl fullWidth>

                    <TextField
                      fullWidth
                      size="small"
                      label="Saldo Disponible Cobro"
                      value={balanceCollect || 0}
                    />
                  </FormControl>

                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="tipogasto">Seleccionar Detalle Operación</InputLabel>
                  {expenses && expenses.length > 0 ? (
                    <Select
                      label="Tipo de Gasto"
                      value={expensesid !== null ? expensesid.toString() : ""}
                      id="Tipo_de_gasto_id"
                      labelId="Tipo_de_gasto_id"
                      onChange={(event) => setExpensesId(parseInt(event.target.value))}
                      size="small"
                      required
                    >
                      {expenses.map((expense: Expense) => (
                        <MenuItem key={expense.id} value={expense.id.toString()}>
                          {expense.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Valor"
                  placeholder="1000"
                  type="number"
                  required
                  onChange={(event) => setValue(event.target.value)}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Descripción"
                  maxRows={3}
                  placeholder="Describa el motivo del gasto"
                  onChange={(event) => setDescripcion(event.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </Grid>
          </CardContent>
          <Divider sx={{ margin: 0 }} />
          <CardActions>
            <Button size='small' type='submit' sx={{ mr: 2 }} variant='contained' onClick={submit}>
              Guardar
            </Button>
            <Link to='/administrar/cajas'>
              <Button size='small' color='secondary' variant='outlined'>
                Cancelar
              </Button>
            </Link>
          </CardActions>
        </form>
      </Card>
    </HeaderResponsive>
  );
};

export default CajaPrincipalAdd;
